import { useCallback, useEffect, useRef } from 'react';

import { useWebsocketSubscription } from './useWebsocketSubscription';

type UseDebouncedWebsocketSubscriptionOptions<T> = {
  channel: string;
  params?: Record<string, unknown>;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onReceived?: (messages: T[]) => void;
  onRejected?: () => void;
  enabled?: boolean;
  debounceMs: number;
};

export const useDebouncedWebsocketSubscription = <T,>({
  channel,
  params,
  onConnected,
  onDisconnected,
  onReceived,
  onRejected,
  enabled,
  debounceMs,
}: UseDebouncedWebsocketSubscriptionOptions<T>) => {
  const pendingMessages = useRef<T[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onReceivedRef = useRef(onReceived);

  onReceivedRef.current = onReceived;

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleReceived = useCallback(
    (data: T) => {
      pendingMessages.current.push(data);

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        onReceivedRef.current?.(pendingMessages.current);
        pendingMessages.current = [];
        timerRef.current = null;
      }, debounceMs);
    },
    [debounceMs]
  );

  return useWebsocketSubscription<T>({
    channel,
    params,
    onConnected,
    onDisconnected,
    onReceived: handleReceived,
    onRejected,
    enabled,
  });
};
